import { useEffect, useState, useRef } from 'react';
import { Router } from 'next/router';

interface UseRouteLoaderOptions {
  delay?: number; // Delay before showing loader (prevents flash for fast navigations)
  minLoadingTime?: number; // Minimum time to show loader (prevents flash)
}

export const useRouteLoader = (options: UseRouteLoaderOptions = {}) => {
  const { delay = 200, minLoadingTime = 500 } = options;
  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const isLoadingRef = useRef(false);
  const currentPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    // Initialize current pathname
    if (typeof window !== 'undefined') {
      currentPathnameRef.current = window.location.pathname;
    }
  }, []);

  useEffect(() => {
    const handleStart = (url: string) => {
      // Don't show loader for hash changes
      if (url.includes('#')) return;

      // Parse the new URL to get pathname
      const newUrl = new URL(url, window.location.origin);
      const newPathname = newUrl.pathname;

      // Only show loader if pathname is actually changing
      if (newPathname === currentPathnameRef.current) {
        return;
      }

      startTimeRef.current = Date.now();
      isLoadingRef.current = true;

      // Delay showing loader to prevent flash for fast navigations
      timeoutRef.current = setTimeout(() => {
        if (isLoadingRef.current) {
          setIsLoading(true);
        }
      }, delay);
    };

    const handleComplete = (url?: string) => {
      // Update current pathname when navigation completes
      if (url) {
        const completedUrl = new URL(url, window.location.origin);
        currentPathnameRef.current = completedUrl.pathname;
      }

      isLoadingRef.current = false;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (startTimeRef.current) {
        const elapsed = Date.now() - startTimeRef.current;
        const remainingTime = Math.max(0, minLoadingTime - elapsed);

        if (remainingTime > 0) {
          // Keep loader visible for minimum time to prevent flash
          setTimeout(() => setIsLoading(false), remainingTime);
        } else {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }

      startTimeRef.current = null;
    };

    Router.events.on('routeChangeStart', handleStart);
    Router.events.on('routeChangeComplete', handleComplete);
    Router.events.on('routeChangeError', handleComplete);

    return () => {
      Router.events.off('routeChangeStart', handleStart);
      Router.events.off('routeChangeComplete', handleComplete);
      Router.events.off('routeChangeError', handleComplete);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [delay, minLoadingTime]);

  return isLoading;
};
